import { useEffect, useRef, useState } from 'react';
import { Bug, Lightbulb, X } from 'lucide-react';
import './UserFeedbackDialog.css';

const MAX_MESSAGE_LENGTH = 4000;
const CATEGORIES = [
  {
    description: 'Tell us about something that did not work.',
    icon: Bug,
    label: 'Report a bug',
    value: 'bug',
  },
  {
    description: 'Share an idea that would improve Telome.',
    icon: Lightbulb,
    label: 'Suggest a feature',
    value: 'feature',
  },
];

const getErrorMessage = (error) =>
  String(
    error?.response?.data?.error ||
      error?.message ||
      'Could not submit feedback right now. Please try again.'
  );

export default function UserFeedbackDialog({ onClose, onSubmit, open }) {
  const closeButtonRef = useRef(null);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    setCategory('');
    setMessage('');
    setErrorText('');
    setSubmitting(false);

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!category || !trimmedMessage || submitting) return;

    setSubmitting(true);
    setErrorText('');
    try {
      await onSubmit({ category, message: trimmedMessage });
      onClose();
    } catch (error) {
      setErrorText(getErrorMessage(error));
      setSubmitting(false);
    }
  };

  return (
    <div className="user-feedback-overlay" role="presentation">
      <button
        aria-label="Close feedback"
        className="user-feedback-backdrop"
        disabled={submitting}
        onClick={onClose}
        type="button"
      />
      <section
        aria-labelledby="user-feedback-title"
        aria-modal="true"
        className="user-feedback-dialog"
        role="dialog"
      >
        <header className="user-feedback-header">
          <div>
            <h2 id="user-feedback-title">Give Feedback</h2>
            <p>Help us make Telome better. We&apos;d love to hear your thoughts.</p>
          </div>
          <button
            aria-label="Close feedback"
            className="user-feedback-close"
            disabled={submitting}
            onClick={onClose}
            ref={closeButtonRef}
            type="button"
          >
            <X size={20} />
          </button>
        </header>

        <form className="user-feedback-form" onSubmit={handleSubmit}>
          <fieldset className="user-feedback-fieldset">
            <legend>What&apos;s on your mind?</legend>
            <div className="user-feedback-categories">
              {CATEGORIES.map((item) => {
                const Icon = item.icon;
                const selected = category === item.value;
                return (
                  <button
                    aria-pressed={selected}
                    className={`user-feedback-category ${selected ? 'is-selected' : ''}`.trim()}
                    disabled={submitting}
                    key={item.value}
                    onClick={() => setCategory(item.value)}
                    type="button"
                  >
                    <span className="user-feedback-category__icon" aria-hidden="true">
                      <Icon size={21} />
                    </span>
                    <span className="user-feedback-category__copy">
                      <strong>{item.label}</strong>
                      <span>{item.description}</span>
                    </span>
                    <span className="user-feedback-radio" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label className="user-feedback-message-label" htmlFor="user-feedback-message">
            <span>Tell us more</span>
            <span>{message.length}/{MAX_MESSAGE_LENGTH}</span>
          </label>
          <textarea
            disabled={submitting}
            id="user-feedback-message"
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Share what happened or what you would like to see..."
            rows={6}
            value={message}
          />

          {errorText ? <p className="user-feedback-error" role="alert">{errorText}</p> : null}

          <button
            className="user-feedback-submit"
            disabled={!category || !message.trim() || submitting}
            type="submit"
          >
            {submitting ? 'Submitting...' : 'Submit feedback'}
          </button>
        </form>
      </section>
    </div>
  );
}
